import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { NotificationsLevel } from "../../../../../lib/enums";
import { route } from "../../../../../shim/jsbridge";
import { show } from "../../../../../notifications/notifications";
import api from "../../../api";
import { PDF_ACTION_TXT } from "../consts";

const CHECK_PDF_STATUS = "checkPdfStatus";
const DOWNLOAD_PDF = "downloadPdf";
const MAKE_PDF = "makePdf";

const CHECK_PDF_STATUS_INTERVAL = 3000;

const { pdf_action } = window?.tcbase?.consts;

const usePdf = (hash?: string, sourceId?: number, relationshipId?: number) => {
  const [interval, setInterval] = useState<number | false>(
    CHECK_PDF_STATUS_INTERVAL
  );
  const [status, setStatus] = useState("make");
  const [isMaking, setIsMaking] = useState(false);

  const handleError = () => {
    show({
      level: NotificationsLevel.ERROR,
      message: PDF_ACTION_TXT.message.error
    });
    setStatus("make");
  };

  const checkPdfQueryFn = () => {
    if (!hash || !sourceId) return undefined;
    return api.checkPdf(hash, sourceId, relationshipId);
  };

  const downloadPdfQuery = useMutation({
    mutationKey: [DOWNLOAD_PDF, hash, sourceId, relationshipId],
    mutationFn: () =>
      api.downloadPdf(hash ?? "", sourceId ?? 0, relationshipId),
    onSuccess: () => {
      const url = route(
        "pdf_summary_get",
        { hash_value: hash, source_id: sourceId },
        relationshipId && { r: relationshipId }
      );
      window.open(url);
    },
    onError: () => {
      handleError();
    }
  });

  const downloadPdf = () => {
    if (!isMaking) return;
    setIsMaking(false);
    show({
      level: NotificationsLevel.INFO,
      message: PDF_ACTION_TXT.message.downloading
    });
    downloadPdfQuery.mutate();
  };

  const makePdfQuery = useMutation({
    mutationKey: [MAKE_PDF, hash, sourceId, relationshipId],
    mutationFn: () => api.makePdf(hash ?? "", sourceId ?? 0, relationshipId),
    onSuccess: () => {
      show({
        level: NotificationsLevel.WARNING,
        message: PDF_ACTION_TXT.message.making
      });
      setIsMaking(true);
      setInterval(CHECK_PDF_STATUS_INTERVAL);
    }
  });

  useQuery({
    queryKey: [CHECK_PDF_STATUS, hash, sourceId, relationshipId],
    queryFn: () =>
      checkPdfQueryFn()?.then(data => {
        const pdfResponseStatus = data.data.status;
        const pdfStatus = Object.keys(window?.tcbase?.consts?.pdf_action)
          .find(key => pdf_action[key] === pdfResponseStatus)
          ?.toLowerCase();

        setStatus(pdfStatus ?? "make");

        if (pdfResponseStatus === pdf_action.DOWNLOAD) {
          downloadPdf();
          setInterval(false);
        } else if (pdfResponseStatus === pdf_action.WAIT) {
          setInterval(CHECK_PDF_STATUS_INTERVAL);
        } else if (pdfResponseStatus === pdf_action.ERROR) {
          handleError();
          setInterval(false);
        } else {
          setInterval(false);
        }

        return data;
      }),
    refetchInterval: interval
  });

  const downloadPdfFn = useCallback(() => {
    if (!hash || !sourceId) return;
    downloadPdfQuery.mutate();
  }, [downloadPdfQuery, hash, sourceId]);

  const makePdfFn = useCallback(() => {
    if (!hash || !sourceId) return;
    makePdfQuery.mutate();
  }, [makePdfQuery, hash, sourceId]);

  return {
    status,
    downloadPdf: downloadPdfFn,
    makePdf: makePdfFn
  };
};

export default usePdf;
